import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useState } from 'react';
import {
  useUpdateABMOrganizationInfoMutation,
  useConfirmOrganizationResellerMutation,
  useLazyGetResellerByAbmIdQuery,
} from '@/redux/slices/organizationApiSlice';
import { ABMEnrollmentStatusEnum } from '@electricjs/core_entity-client';
import { Country } from '@electricjs/arc';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TFunction } from 'i18next';
import { CA_ABM_RESELLER_ID, US_ABM_RESELLER_ID } from '@/constants/abm';

const US_COUNTRY_CODE: Country = 'US';
const CA_COUNTRY_CODE: Country = 'CA';

type UseABMSetupType = {
  onSaveAbm: () => void;
  onErrorAbm: () => void;
  abmOrgID?: string;
  isCheckboxSelected?: boolean;
  t: TFunction;
};

/** Hook to validate ABM setup */
export const useABMSetup = ({
  onSaveAbm,
  onErrorAbm,
  abmOrgID = '',
  isCheckboxSelected = false,
  t,
}: UseABMSetupType) => {
  const organizationId = useGetOrganizationId();
  const { abmCanadaResellerId } = useFlags();
  const [isChecked, setIsChecked] = useState(isCheckboxSelected);
  const [needsThirdParty, setNeedsThirdParty] = useState(false);
  const [needsValidAbmID, setNeedsValidAbmID] = useState(false);
  const [abmIDErrorMessage, setAbmIDErrorMessage] = useState('');
  const [abmID, setAbmID] = useState(abmOrgID);
  const [isLoading, setIsLoading] = useState(false);

  // Disables the save button when the user has not edited the abm ID (saved abmOrgID is the same as input value)
  const needsEditAbmID = abmID === abmOrgID;

  const [updateABMOrganizationInfo] = useUpdateABMOrganizationInfoMutation();
  const [confirmOrganizationReseller] =
    useConfirmOrganizationResellerMutation();
  const [getResellerByAbmId] = useLazyGetResellerByAbmIdQuery();

  // Resets state when changing ABM status (e.g. DEFAULT to SETUP and back to DEFAULT)
  const handleResetState = () => {
    setNeedsThirdParty(false);
    setNeedsValidAbmID(false);
    setIsChecked(false);
    setAbmIDErrorMessage('');
    setAbmID('');
  };

  const handleCheckbox = () => {
    setIsChecked(!isChecked);
    needsThirdParty && setNeedsThirdParty(false);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNeedsValidAbmID(false);
    setAbmID(e.currentTarget.value);
  };

  const isNum = /^\d+$/.test(abmID);

  const handleSave = () => {
    if (!needsThirdParty && !isChecked) {
      setNeedsThirdParty(true);
    }
    if (abmID.length < 4 || abmID.length > 50 || !isNum) {
      setNeedsValidAbmID(true);
      abmID.length < 4 &&
        setAbmIDErrorMessage(t('useABMSetup.validation.minLength'));
      abmID.length > 50 &&
        setAbmIDErrorMessage(t('useABMSetup.validation.maxLength'));
      !isNum && setAbmIDErrorMessage(t('useABMSetup.validation.numericOnly'));
      return;
    }
    if (!needsThirdParty && !needsValidAbmID && isNum && isChecked) {
      setIsLoading(true);

      const abmResellerIdsToConfirm = [
        { abmResellerId: US_ABM_RESELLER_ID, countryCode: US_COUNTRY_CODE },
        ...(abmCanadaResellerId
          ? [
              {
                abmResellerId: CA_ABM_RESELLER_ID,
                countryCode: CA_COUNTRY_CODE,
              },
            ]
          : []),
      ];

      Promise.all(
        abmResellerIdsToConfirm.map(({ abmResellerId, countryCode }) =>
          getResellerByAbmId({
            organizationId,
            abmResellerId,
            countryCode,
          }).unwrap()
        )
      )
        .then(resellers =>
          confirmOrganizationReseller({
            organizationId,
            resellerIds: resellers.map(reseller => reseller.id),
          }).unwrap()
        )
        .then(() =>
          updateABMOrganizationInfo({
            organizationId,
            abmID: Number.parseInt(abmID),
            abmStatus: ABMEnrollmentStatusEnum.Connected,
          }).unwrap()
        )
        .then(() => {
          setIsLoading(false);
          onSaveAbm();
        })
        .catch(() => {
          setIsLoading(false);
          onErrorAbm();
        });
    }
  };

  return {
    abmIDErrorMessage,
    handleCheckbox,
    handleInput,
    handleSave,
    handleResetState,
    needsValidAbmID,
    needsThirdParty,
    needsEditAbmID,
    isLoading,
  };
};
